import React from "react";
import LegalLayout from "@/components/legal/LegalLayout";

export default function TermsPublisher() {
  return (
    <LegalLayout title="Publisher Terms of Service" eyebrow="Legal · Publishers" lastUpdated="1 March 2026" version="1.0">
      <p>
        These Publisher Terms of Service ("<strong>Terms</strong>") form a legally binding agreement between{" "}
        <strong>Connexis Group Ltd</strong>, company number <strong>17214446</strong> ("<strong>Connexis</strong>"),
        and the business entity (the "<strong>Publisher</strong>") that supplies consumer enquiries to the Connexis
        platform via API in exchange for a revenue share.
      </p>

      <h2>1. The service</h2>
      <p>
        Connexis provides the Publisher with API credentials to ingest consumer enquiries. Connexis matches each
        enquiry to a single buyer and, where accepted by the buyer, pays the Publisher a percentage of the buyer-paid
        sector CPL.
      </p>

      <h2>2. Revenue share &amp; payment</h2>
      <ul>
        <li>Default revenue share: <strong>90% of the buyer-paid CPL</strong> on each accepted Lead. Connexis retains 10%.</li>
        <li>Revenue share may be varied per-publisher and is shown in the publisher portal.</li>
        <li>Earnings accrue in the Publisher's portal ledger and are withdrawable weekly via Stripe Connect or bank transfer (minimum £50 payout).</li>
        <li>Earnings <strong>do not</strong> accrue on Leads that are rejected by the matched buyer for a documented quality reason and refunded within 72 hours.</li>
        <li>Connexis is responsible for paying its own taxes (corporation tax, VAT). The Publisher is responsible for taxes payable on revenue-share income in its jurisdiction.</li>
      </ul>

      <h2>3. Lead quality &amp; standards</h2>
      <p>The Publisher warrants and undertakes that every Lead submitted:</p>
      <ul>
        <li>Has been generated with explicit, recorded consumer consent (TrustedForm or equivalent certificate URL).</li>
        <li>Originated from a clearly disclosed source landing page in compliance with UK GDPR, PECR and applicable sector rules.</li>
        <li>Is not duplicated within a 7-day rolling window (Connexis dedupes automatically and rejects duplicates).</li>
        <li>Is not generated by incentivised traffic, co-registration, or bot/synthetic traffic.</li>
        <li>Is not generated via misleading creative or unauthorised use of the Connexis brand or buyer names.</li>
      </ul>

      <h2>4. Caps &amp; quality control</h2>
      <ul>
        <li>Connexis may apply daily and monthly volume caps per publisher.</li>
        <li>Connexis maintains a Publisher Quality Score (accept rate, kept rate, disposition mix). Persistently low quality may trigger automatic suspension.</li>
        <li>If buyer return rate exceeds 15% over any rolling 30-day window, Connexis may pause ingestion pending investigation.</li>
      </ul>

      <h2>5. API access &amp; security</h2>
      <ul>
        <li>The Publisher is responsible for safeguarding its API key. Compromised keys must be rotated immediately via the portal.</li>
        <li>Rate limits: 600 requests per minute per publisher by default.</li>
        <li>Connexis may revoke API access immediately in the event of suspected fraud or abuse.</li>
      </ul>

      <h2>6. Confidentiality &amp; data</h2>
      <p>
        Buyer identities, individual Lead acceptance rates and routing logic are confidential. The Publisher's
        relationship with Connexis (under these Terms) and processing of Lead data is further governed by our{" "}
        <a href="/legal/dpa">Data Processing Agreement</a>.
      </p>

      <h2>7. Term &amp; termination</h2>
      <ul>
        <li>This agreement is open-ended and may be terminated by either party for convenience on 14 days written notice.</li>
        <li>Earnings accrued up to the termination date will be paid in the next scheduled payout, subject to standard quality reconciliation.</li>
        <li>Connexis may suspend or terminate immediately for material breach of clauses 3 (Quality) or 5 (Security).</li>
      </ul>

      <h2>8. Liability</h2>
      <p>
        Each party's aggregate liability is capped at the total revenue paid by Connexis to the Publisher in the 12
        months preceding the claim. Nothing limits liability for death, personal injury, fraud or wilful misconduct.
      </p>

      <h2>9. Governing law</h2>
      <p>
        Laws of England and Wales. Exclusive jurisdiction of the English courts.
      </p>

      <h2>10. Notices</h2>
      <p>
        Connexis: <a href="mailto:legal@connex.is">legal@connex.is</a><br />
        Publisher: the email registered on the publisher account.
      </p>

      <p className="!mt-12 !text-[13px] !text-slate-500">
        These Terms are signed on behalf of Connexis Group Ltd by its appointed director. The Publisher's
        authorised signatory accepts these Terms by completing the e-signature flow in the publisher portal.
      </p>
    </LegalLayout>
  );
}
